import Button from 'components/Button';
import FooterBasic from 'components/Footers/Basic';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import SubmitButton from 'components/Form/SubmitButton';
import HeaderBasic from 'components/Headers/Basic';
import DualPage from 'components/Layout/DualPage';
import Stacked from 'components/Layout/Stacked';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiError} from 'utils/request-axios/utils';

import onboardingImage from '../assets/img/onboarding-2.png';
import {
    CompleteFormData,
    PartialFormData,
    PreviewFormData,
    SetupBackendData,
} from '../types';
import WizardSectionHeader from '../WizardSectionHeader';
import SenderInformation from './SenderInformation';
import {usePostProfileSetup} from './services';

type PreviewProps = {
    onAddNewAddress: () => void;
    onBack: () => void;
    onDelete: () => void;
    onEditExistingNewAddress: (
        target: 'partnerAddress' | 'signupAddress',
    ) => void;
    onFormatChange: (target: 'partnerAddress' | 'signupAddress') => void;
    prefillData: PartialFormData;
};

const Preview: FC<PreviewProps> = ({
    onAddNewAddress,
    onBack,
    onDelete,
    onEditExistingNewAddress,
    onFormatChange,
    prefillData,
}) => {
    const methods = useForm<FormFields<PreviewFormData>>({
        shouldUnregister: true,
    });
    const {handleSubmit} = methods;

    const {
        data: {contactName, mid, prefectures},
    } = useBackend<Backend<SetupBackendData>>();

    const {mutateAsync} = usePostProfileSetup(mid);

    const onSubmit = async (formData: PreviewFormData) => {
        const {senderInformation} = formData;

        const {partnerAddress, signupAddress} = prefillData.addresses;

        const {
            partnerFaxNumber,
            partnerPhoneNumber,
            signupBusinessEmail,
            signupFaxNumber,
            signupPhoneNumber,
        } = prefillData;

        let completeData: CompleteFormData = {signupAddress};

        if (senderInformation !== 'businessNameOnly') {
            const useSignup = senderInformation === 'signupAddress';

            completeData = {
                partnerAddress: useSignup ? signupAddress : partnerAddress,
                partnerEmail: signupBusinessEmail,
                partnerFaxNumber: useSignup
                    ? signupFaxNumber
                    : partnerFaxNumber,
                partnerPhoneNumber: useSignup
                    ? signupPhoneNumber
                    : partnerPhoneNumber,
                signupAddress,
            };
        }

        mutateAsync(completeData)
            .then(() => {
                setUrl(window.Urls.new_document_create_view('invoice'));
            })
            .catch(onApiError(methods));
    };

    return (
        <div className="relative flex min-h-screen flex-col bg-grey-100 ">
            <HeaderBasic className="px-8" showSignIn={false} />
            <DualPage
                classNameFirstSection="bg-gradient-to-tr from-green-800 to-green-600"
                classNameInner="overflow-hidden rounded-lg"
                classNameSecondSection="bg-white"
                pagination={{
                    current: 3,
                    length: 3,
                }}
                size="6xl"
            >
                <>
                    <img
                        alt="post-signup-wizard-img"
                        className="invisible absolute bottom-0 w-full md:visible"
                        src={onboardingImage}
                    />
                    <WizardSectionHeader
                        subTitle={t`This is a preview of how your information will be displayed on the documents you send to your customers.`}
                        title={t`Great - you're ready to send your first invoice!`}
                    />
                </>
                <FormProvider {...methods}>
                    <form
                        className="bg-white p-6"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <FormHeader
                            className="mt-0"
                            hideHR={true}
                            subtitle={t`We use your business address by default. You can change this anytime later.`}
                            title={t`Sender information preview`}
                        />
                        <div className="mt-4 grid gap-y-2">
                            <SenderInformation
                                {...prefillData}
                                contactName={contactName}
                                onDelete={onDelete}
                                onEdit={onEditExistingNewAddress}
                                onFormatChange={onFormatChange}
                                prefectures={prefectures}
                            />
                            {!prefillData.addresses.partnerAddress && (
                                <div className="flex">
                                    <Button
                                        className="border-none text-sm text-green-700"
                                        icon="add_solid"
                                        kind="custom"
                                        onClick={onAddNewAddress}
                                    >
                                        {t`Add a separate invoicing contact`}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <Stacked className="mt-4 w-full">
                            <FormResult />
                            <div className="flex flex-1 flex-col-reverse justify-between gap-6 md:grid-cols-2  md:flex-row">
                                <Button kind="tertiary" onClick={onBack}>
                                    {t`Go back`}
                                </Button>
                                <SubmitButton>
                                    {t`Create your first invoice`}
                                </SubmitButton>
                            </div>
                        </Stacked>
                    </form>
                </FormProvider>
            </DualPage>
            <FooterBasic />
        </div>
    );
};

export default Preview;
